import { Button } from "@comps/ui/button";
import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Separator } from "@comps/ui/separator";
import { BarChartData, BarChartWidgetType, KPIWidgetType, LineChartData, LineChartWidgetType, PieChartWidgetType, RadarChartData, RadarChartWidgetType, TextWidgetType, Widget, WidgetType, WidgetTypes } from "@dto/Dashboard";
import { getWsButtonStyle, TW_Button, TW_WidgetParameterLabel, TW_WidgetParameterValue, TW_WidgetSectionLabel, useHover } from "@utils/twClasses";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { KPICard } from "./KPICard";
import { KPICardConfig } from "./KPICardConfig";
import { PieChartCard } from "./PieChartCard";
import { PieChartCardConfig } from "./PieChartCardConfig";
import { useModelStore } from "src/store";
import { Switch } from "@comps/ui/switch";
import { BarChartCardConfig } from "./BartChartCardConfig";
import { BarChartCard } from "./BarChartCard";
import { RadarChartCardConfig } from "./RadarChartCardConfig";
import { RadarChartCard } from "./RadarChartCard";
import { LineChartCard } from "./LineChartCard";
import { LineChartCardConfig } from "./LineChartCardConfig";
import { TextCard } from "./TextCard";
import { TextCardConfig } from "./TextCardConfig";
import { GRAY_300_COLOR } from "@utils/colors";

export interface WidgetConfigProps {
    initialWidget: Widget | undefined;
    onCancel: () => void;
    onValidation: (widget: Widget) => void;
}

export interface WidgetCardConfigProps {
    initialWidget: Widget | undefined;
    onConfigChange: (widget: Widget | undefined) => void;
}

type DefaultDimension = {
    width: number | null;
    height: number | null;
}

const DEFAULT_DIMENSIONS: Map<WidgetType, DefaultDimension> = new Map([
    [KPIWidgetType, { width: 200, height: null }],
    [PieChartWidgetType, { width: 400, height: 400 }],
    [BarChartWidgetType, { width: 550, height: 400 }],
    [RadarChartWidgetType, { width: 400, height: 400 }],
    [LineChartWidgetType, { width: 550, height: 400 }],
    [TextWidgetType, { width: 400, height: null }],
]);

export function WidgetConfig({ initialWidget, onValidation }: WidgetConfigProps) {

    // const workspace = useModelStore(state => state.selectedWorkspace);
    // const dashboards = useModelStore(state => state.dashboards);
    // const updateDashboards = useModelStore(state => state.updateDashboards);
    // const updateSelectedDashboard = useModelStore(state => state.updateSelectedDashboard);
    const [widget, setWidget] = useState<Widget | undefined>(initialWidget);
    const [widgetLabel, setWidgetLabel] = useState<string>(initialWidget?.title || "");
    const [widgetDescription, setWidgetDescription] = useState<string>(initialWidget?.description || "");
    const [widgetType, setWidgetType] = useState<WidgetType | undefined>(initialWidget?.type);
    const [widgetWidth, setWidgetWidth] = useState<number | null>(initialWidget?.width ?? null);
    const [widgetHeight, setWidgetHeight] = useState<number | null>(initialWidget?.height ?? null);
    const [widgetShowLegend, setWidgetShowLegend] = useState<boolean>(initialWidget?.showLegend ?? true);
    const [widgetShowValues, setWidgetShowValues] = useState<boolean>(initialWidget?.showValues ?? false);
    const [widgetShowPercentage, setWidgetShowPercentage] = useState<boolean>(initialWidget?.showPercentage ?? false);
    const [widgetHideYAxis, setWidgetHideYAxis] = useState<boolean>(initialWidget?.hideYAxis === true);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const validationButtonHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const { t } = useTranslation();
    const style = {
        scrollbarColor: wsColor + " " + wsLightColor
    }

    function onWidgetChange(widget: Widget | undefined) {
        if (widget) {
            widget.title = widgetLabel;
            widget.description = widgetDescription;
            widget.width = widgetWidth;
            widget.height = widgetHeight;
            widget.showLegend = widgetShowLegend;
            widget.showValues = widgetShowValues;
            widget.showPercentage = widgetShowPercentage;
            widget.hideYAxis = widgetHideYAxis;
            setWidget(widget);
        } else {
            setWidget(undefined);
        }
    }

    function onWidgetLabelChange(label: string) {
        setWidgetLabel(label);
        if (widget) {
            widget.title = label;
            setWidget(widget);
        }
    }


    const onWidthChange = (value: string) => {
        const width = (value === "") ? null : parseInt(value);
        setWidgetWidth(width);
        if (widget) {
            widget.width = width;
            setWidget(widget);
        }
    }

    const onHeightChange = (value: string) => {
        const height = (value === "") ? null : parseInt(value);
        setWidgetHeight(height);
        if (widget) {
            widget.height = height;
            setWidget(widget);
        }
    }

    const onShowLegendChange = (value: boolean) => {
        setWidgetShowLegend(value);
        if (widget) {
            widget.showLegend = value;
            setWidget(widget);
        }
    }

    const onShowValuesChange = (value: boolean) => {
        setWidgetShowValues(value);
        if (widget) {
            widget.showValues = value;
            setWidget(widget);
        }
    }

    const onShowPercentageChange = (value: boolean) => {
        setWidgetShowPercentage(value);
        if (widget) {
            widget.showPercentage = value;
            setWidget(widget);
        }
    }

    const onHideYAxisChange = (value: boolean) => {
        setWidgetHideYAxis(value);
        if (widget) {
            widget.hideYAxis = value;
            setWidget(widget);
        }
    }


    const onWidgetTypeChange = (value: string | undefined) => {
        if (value) {
            const widgetType = value as WidgetType;
            setWidgetType(widgetType);
            if (widgetLabel === "") {
                setWidgetLabel(t(`dashboard.widget.default.label.${widgetType}`));
            }
            setWidgetWidth(DEFAULT_DIMENSIONS.get(widgetType)?.width ?? null);
            setWidgetHeight(DEFAULT_DIMENSIONS.get(widgetType)?.height ?? null);
        } else {
            setWidgetType(undefined);
            setWidgetWidth(null);
            setWidgetHeight(null);
        }
        setWidget(undefined);
    }

    function renderWidgetConfig(widgetType: WidgetType) {
        switch (widgetType) {
            case KPIWidgetType:
                return <KPICardConfig initialWidget={initialWidget} onConfigChange={onWidgetChange} />;
            case PieChartWidgetType:
                return <PieChartCardConfig initialWidget={initialWidget} onConfigChange={onWidgetChange} />;
            case BarChartWidgetType:
                return <BarChartCardConfig initialWidget={initialWidget} onConfigChange={onWidgetChange} />;
            case LineChartWidgetType:
                return <LineChartCardConfig initialWidget={initialWidget} onConfigChange={onWidgetChange} />;
            case RadarChartWidgetType:
                return <RadarChartCardConfig initialWidget={initialWidget} onConfigChange={onWidgetChange} />;
            case TextWidgetType:
                return <TextCardConfig initialWidget={initialWidget} onConfigChange={onWidgetChange} />;
            default:
                return null;
        }
    }

    function renderWidgetOverview(widget: Widget) {
        switch (widget.type) {
            case KPIWidgetType:
                return <KPICard widget={widget} />;
            case PieChartWidgetType:
                return <PieChartCard widget={widget} />;
            case BarChartWidgetType:
                return <BarChartCard widget={widget} />;
            case RadarChartWidgetType:
                return <RadarChartCard widget={widget} />;
            case LineChartWidgetType:
                return <LineChartCard widget={widget} />;
            case TextWidgetType:
                return <TextCard widget={widget} />;
            default:
                return null;
        }
    }


    function canHaveLegend(widget: Widget): boolean {
        if (widget.type === KPIWidgetType || widget.type === TextWidgetType) {
            return false;
        }
        if (widget.type === BarChartWidgetType) {
            const data = widget.data as BarChartData;
            return data.categoryByYear || (data.categoryPath != null && data.categoryPath !== undefined);
        }

        if (widget.type === RadarChartWidgetType) {
            const data = widget.data as RadarChartData;
            return data.categoryByYear || (data.categoryPath != null && data.categoryPath !== undefined);
        }

        if (widget.type === LineChartWidgetType) {
            const data = widget.data as LineChartData;
            return data.categoryByYear || (data.categoryPath != null && data.categoryPath !== undefined);
        }
        return true;
    }

    function canDisplayPercentage(widget: Widget): boolean {
        if (widget.type === PieChartWidgetType || widget.type === LineChartWidgetType) {
            return true;
        }
        return false;
    }

    function canDisplayValues(widget: Widget): boolean {
        if (widget.type === PieChartWidgetType || widget.type === BarChartWidgetType || widget.type === LineChartWidgetType) {
            return true;
        }
        return false;
    }

    function canHideYAxis(widget: Widget): boolean {
        if (widget.type === BarChartWidgetType || widget.type === LineChartWidgetType) {
            return true;
        }
        return false;
    }

    function hasTitle(widgetType: WidgetType): boolean {
        return widgetType != TextWidgetType;
    }

    return (
        <>
            <div className="flex flex-row justify-start items-center gap-2 w-full h-[calc(100%-theme('spacing.8'))]">
                <div className="flex flex-col justify-start items-start gap-4 w-2/5 h-full overflow-auto scrollbar-thin scrollbar-thumb-compozz-purple scrollbar-track-compozz-purple-light scrollbar-button-hidden px-2" style={style}>
                    <div className="flex flex-col justify-start items-start gap-2 w-full">
                        <Label className={TW_WidgetSectionLabel}>{t("dashboard.configuration.main.info.label")}</Label>
                        {widgetType && hasTitle(widgetType) && 
                            <>
                                <Label>{t("dashboard.widget.label.title")}</Label>
                                <Input value={widgetLabel} onChange={(e) => onWidgetLabelChange(e.target.value)} />
                            </>
                        }
                        <Label>{t("dashboard.widget.type.title")}</Label>
                        <Select value={widgetType} onValueChange={onWidgetTypeChange}>
                            <SelectTrigger>
                                <SelectValue placeholder={t("dashboard.widget.type.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                {WidgetTypes.map(widgetType => (
                                    <SelectItem key={widgetType} value={widgetType}>{t(`dashboard.widget.type.label.${widgetType}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {widgetType && renderWidgetConfig(widgetType)}
                    </div>
                    {widgetType &&
                        <div className="flex flex-col justify-start items-start gap-2 w-full">
                            <Label className={TW_WidgetSectionLabel}>{t("dashboard.dimension.label")}</Label>
                            <div className="flex flex-row justify-start items-center gap-1 w-full">
                                <Label className={TW_WidgetParameterLabel}>{t("dashboard.dimension.label.width")}</Label>
                                <Input className={TW_WidgetParameterValue} value={widgetWidth?.toString() ?? ""} onChange={(e) => onWidthChange(e.target.value)} />
                            </div>
                            <div className="flex flex-row justify-start items-center gap-1 w-full">
                                <Label className={TW_WidgetParameterLabel}>{t("dashboard.dimension.label.height")}</Label>
                                <Input className={TW_WidgetParameterValue} value={widgetHeight?.toString() ?? ""} onChange={(e) => onHeightChange(e.target.value)} />
                            </div>
                        </div>
                    }

                    {widget && (canHaveLegend(widget) || canDisplayValues(widget) || canHideYAxis(widget) || canDisplayPercentage(widget)) &&
                        <div className="flex flex-col justify-start items-start gap-2 w-full">
                            <Label className={TW_WidgetSectionLabel}>{t("dashboard.legend.label")}</Label>
                            {canHaveLegend(widget) && <div className="flex flex-row justify-start items-center gap-1 w-full">
                                <Label className={TW_WidgetParameterLabel}>{t("dashboard.show.legend.label")}</Label>
                                <Switch id="show-legend" checked={widgetShowLegend} onCheckedChange={onShowLegendChange} style={{ backgroundColor: (widgetShowLegend ? wsColor : GRAY_300_COLOR) }} />
                            </div>}
                            {canDisplayValues(widget) && <div className="flex flex-row justify-start items-center gap-1 w-full">
                                <Label className={TW_WidgetParameterLabel}>{t("dashboard.show.values.label")}</Label>
                                <Switch id="show-values" checked={widgetShowValues} onCheckedChange={onShowValuesChange} style={{ backgroundColor: (widgetShowValues ? wsColor : GRAY_300_COLOR) }} />
                            </div>}
                            {canDisplayPercentage(widget) && <div className="flex flex-row justify-start items-center gap-1 w-full">
                                <Label className={TW_WidgetParameterLabel}>{t("dashboard.show.percentage.label")}</Label>
                                <Switch id="show-percentage" checked={widgetShowPercentage} onCheckedChange={onShowPercentageChange} style={{ backgroundColor: (widgetShowPercentage ? wsColor : GRAY_300_COLOR) }} />
                            </div>}
                            {canHideYAxis(widget) && <div className="flex flex-row justify-start items-center gap-1 w-full">
                                <Label className={TW_WidgetParameterLabel}>{t("dashboard.hide.y.axis.label")}</Label>
                                <Switch id="hide-y-axis" checked={widgetHideYAxis} onCheckedChange={onHideYAxisChange} style={{ backgroundColor: (widgetHideYAxis ? wsColor : GRAY_300_COLOR) }} />
                            </div>}
                        </div>
                    }
                </div>
                <Separator orientation="vertical" className="h-full" />
                <div className="flex flex-col justify-between items-start gap-2 w-3/5 h-full overflow-auto scrollbar-thin scrollbar-thumb-compozz-purple scrollbar-track-compozz-purple-light scrollbar-button-hidden px-2" style={style}>
                    <div className="flex flex-col justify-start items-start gap-2 w-full">
                        <Label className={TW_WidgetSectionLabel}>{t("dashboard.overview.label")}</Label>
                        {widget && renderWidgetOverview(widget)}
                        {(!widget) &&
                            <Label>{t("dashboard.overview.no.data")}</Label>
                        }
                    </div>
                    {widget &&
                        <div className="flex flex-row justify-end items-center gap-2 w-full">
                            <Button className={TW_Button} onClick={() => onValidation(widget)} {...validationButtonHover} >
                                {initialWidget ? t("dashboard.button.widget.update") : t("dashboard.button.widget.add")}
                            </Button>
                        </div>
                    }
                </div>
            </div >
        </>
    );
}